import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { first, map, Observable, Subscription } from 'rxjs';
import { ChartConfiguration } from 'src/app/pump-simulation/chartConfiguration';
import { createConfiguration, plotData } from 'src/app/shared/util';
import { setCurrentMeasuredData, setCurrentCalculatedData } from '../../store/charts/current-chart/current-chart.actions';
import { CurrentState } from '../../store/charts/current-chart/current-chart.reducer';
import { selectCurrent } from '../../store/charts/current-chart/current-chart.selector';

declare const google: any;

const CURRENT_CHART_ID = 'currentChart';

@Component({
    selector: 'app-current-chart',
    templateUrl: './current-chart.component.html',
    styleUrls: ['./current-chart.component.scss'],
    standalone: false
})
export class CurrentChartComponent implements OnInit, OnDestroy {

  private subscriptions: Subscription[] = [];
  xAxisToYaxisChart = null;
  showPlaceholder$: Observable<Boolean>;
  @ViewChild(CURRENT_CHART_ID, { static: true }) chartElement: ElementRef;

  constructor(
    private store: Store,
  ) { }

  ngOnInit() {
    this.showPlaceholder$ = this.store.pipe(
      select(selectCurrent),
      map((data: CurrentState) => data.loading)
    );

    const current = this.store.pipe(
      select(selectCurrent),
      map((data: CurrentState) => data)
    ).subscribe(data => {
      if (data.currentMeasuredData && data.currentCalculatedData) {
        this.createCurrentGraph();
      }
    });
    this.subscriptions.push(current);
  }

  createCurrentGraph() {

    const currentChartConfiguration = createConfiguration({
      xAxis: 'electrical power [W]',
      yAxis: 'current [A]',
      height: 300
    });

    const currentGraph = this.store.pipe(
      select(selectCurrent),
      map((data: CurrentState) => data),
      first()
    ).subscribe(data => {
      if (data.currentMeasuredData && data.currentCalculatedData) {
        google.charts.load('current', { packages: ['corechart'] });
        google.charts.setOnLoadCallback(this.drawChart(currentChartConfiguration, this.chartElement.nativeElement, data.currentCalculatedData, data.currentMeasuredData));
      }
    });
    this.subscriptions.push(currentGraph);
  }

  drawChart(
    chartConfiguration: ChartConfiguration,
    chart: ElementRef,
    calculation: Map<number, Map<number, number>>,
    measurements: Map<number, Map<number, number>>
  ) {
    return () => {
      let columnIndex = 1;
      let rowIndex = 0;
      const colors = chartConfiguration.colours;
      const data = new google.visualization.DataTable();
      data.addColumn('number', chartConfiguration.xAxisTitle);
      const options = chartConfiguration.options;

      let head;
      ({ head, rowIndex, columnIndex } = plotData(
        calculation, data, rowIndex, chartConfiguration,
        columnIndex, options, colors, 'calculated'));
      ({ head, rowIndex, columnIndex } = plotData(
        measurements, data, rowIndex, chartConfiguration,
        columnIndex, options, colors, 'measured'));

      this.xAxisToYaxisChart = new google.visualization.LineChart(chart);
      google.visualization.events.addListener(this.xAxisToYaxisChart, 'ready', () => {
        // remove loading
      });
      this.xAxisToYaxisChart.draw(data, options);
    };
  }

  ngOnDestroy() {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
    if(this.xAxisToYaxisChart) {
      this.xAxisToYaxisChart.clearChart();
    }
    this.store.dispatch(setCurrentMeasuredData({ data: null }));
    this.store.dispatch(setCurrentCalculatedData({ data: null }));
  }
}
